/**
 * Manages interactive elements in the header.
 * Handles opening/closing of sub-menus, the quick search panel, and the overlay.
 * Ensures accessibility via ARIA attributes and keyboard navigation.
 */
import { getLenisInstance } from '../../core/scroll-manager';

export function initializeInteractiveHeader() {
    const header = document.getElementById('header');
    if (!header) return;

    // --- Elements ---
    const menuBg = header.querySelector('.header__menu-bg');
    const menuToggles = header.querySelectorAll('.header__menu-toggle');
    const searchToggle = header.querySelector('.header__search-toggle');

    // Select top-level menu items that have children (Triggers)
    // Targeting both Primary and Secondary navigations
    const primaryTriggers = header.querySelectorAll('.header__primary > ul > li.menu-item-has-children > a');
    const secondaryTriggers = header.querySelectorAll('.header__secondary > ul > li.menu-item-has-children > a');
    const navTriggers = [...primaryTriggers, ...secondaryTriggers];

    // Collect all sub-menu panels associated with triggers
    const subMenus = [];
    navTriggers.forEach(trigger => {
        // The walker generates IDs like 'submenu-{ID}' and puts aria-controls on the link
        const panelId = trigger.getAttribute('aria-controls');
        if (panelId) {
            const panel = document.getElementById(panelId);
            if (panel) subMenus.push(panel);
        }
    });

    let activeTrigger = null; // Track which element opened the panel for focus restoration

    // --- State Management ---

    /**
     * Closes all interactive panels (sub-menus, quick search, bg).
     */
    const closeAllPanels = () => {
        // Close all sub-menus
        subMenus.forEach(panel => {
            panel.classList.remove('is-visible');
            panel.setAttribute('aria-hidden', 'true');
        });

        // Remove global menu-open class
        document.body.classList.remove('menu-open');
        
        menuToggles.forEach(toggle => {
            toggle.setAttribute('aria-expanded', 'false');
        });

        // Reset nav triggers
        navTriggers.forEach(trigger => {
            trigger.classList.remove('is-active');
            trigger.setAttribute('aria-expanded', 'false');
        });

        // Resume scrolling
        const lenis = getLenisInstance();
        if (lenis) lenis.start();

        // Restore focus if applicable
        if (activeTrigger && document.body.contains(activeTrigger)) {
            activeTrigger.focus();
        }
        activeTrigger = null;
    };

    /**
     * Opens a specific sub-menu panel.
     * @param {HTMLElement} trigger - The link element that was clicked.
     */
    const openSubMenu = (trigger) => {
        const panelId = trigger.getAttribute('aria-controls');
        const panel = document.getElementById(panelId);
        if (!panel) return;

        // If this specific panel is already open, close everything (toggle behavior)
        if (panel.classList.contains('is-visible')) {
            if (window.innerWidth <= 640) {
                panel.classList.remove('is-visible');
                panel.setAttribute('aria-hidden', 'true');
                trigger.classList.remove('is-active');
                trigger.setAttribute('aria-expanded', 'false');
            } else {
                closeAllPanels();
            }
            return;
        }

        // Close any other open sub-menus first (prevent overlap)
        subMenus.forEach(p => {
            if (p !== panel) {
                p.classList.remove('is-visible');
                p.setAttribute('aria-hidden', 'true');
            }
        });
        
        // Reset other triggers
        navTriggers.forEach(t => {
            if (t !== trigger) {
                t.classList.remove('is-active');
                t.setAttribute('aria-expanded', 'false');
            }
        });

        // Activate current
        panel.classList.add('is-visible');
        panel.setAttribute('aria-hidden', 'false');
        
        trigger.classList.add('is-active');
        trigger.setAttribute('aria-expanded', 'true');
        activeTrigger = trigger;

        // Apply global menu-open class
        document.body.classList.add('menu-open');

        menuToggles.forEach(toggle => {
            toggle.setAttribute('aria-expanded', 'true');
        });

        // Stop scrolling
        const lenis = getLenisInstance();
        if (lenis) lenis.stop();
    };

    // --- Event Listeners ---

    // 1. Nav Triggers
    primaryTriggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
            e.preventDefault();
            openSubMenu(trigger);
        });
    });

    secondaryTriggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
            e.preventDefault();
            openSubMenu(trigger);
        });
    });

    // 2. Menu Toggles (Open/Close)
    menuToggles.forEach(toggle => {
        toggle.addEventListener('click', (e) => {
            e.preventDefault();
            
            if (document.body.classList.contains('menu-open')) {
                closeAllPanels();
            } else {
                document.body.classList.add('menu-open');
                menuToggles.forEach(t => t.setAttribute('aria-expanded', 'true'));
                
                const lenis = getLenisInstance();
                if (lenis) lenis.stop();
                
                activeTrigger = toggle;
            }
        });
    });

    // 3. Close Button (Search Toggle)
    if (searchToggle) {
        searchToggle.addEventListener('click', (e) => {
            e.preventDefault();
            closeAllPanels();
        });
    }

    // 4. Overlay Click
    if (menuBg) {
        menuBg.addEventListener('click', closeAllPanels);
    }

    // 5. Keyboard Navigation (Escape)
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            const isAnyPanelOpen = document.body.classList.contains('menu-open');
            if (isAnyPanelOpen) {
                closeAllPanels();
            }
        }
    });

    // 6. Close on Page Transition
    document.addEventListener('barba-leave-start', () => {
        closeAllPanels();
    });

    // 7. Close on Sub-menu Link Click
    header.querySelectorAll('.header .sub-menu a').forEach(link => {
        link.addEventListener('click', closeAllPanels);
    });
}
